import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, FolderLock, BarChart3, Upload, Trash2, Plus, Download } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

const emptyForm = { email: "", docs_audience: "", doc_grants: [], doc_denies: [], nda_signed: false };

export default function DocsAccessPanel() {
  const [sub, setSub] = useState("access"); // access | vault | report
  const [tiers, setTiers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [vault, setVault] = useState([]);
  const [vForm, setVForm] = useState({ title: "", audiences: [], file: null });
  const [report, setReport] = useState({ items: [], summary: [] });
  const [busy, setBusy] = useState(false);

  const loadAccess = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/docs/access");
      setTiers(data.tiers || []); setCatalog(data.catalog || []);
      setRows((data.items || []).filter((r) => r.docs_audience || (r.doc_grants || []).length || (r.doc_denies || []).length || r.nda_signed));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load access"); }
  }, []);
  const loadVault = useCallback(async () => {
    try { const { data } = await api.get("/admin/docs/vault"); setVault(data.items || []); } catch { /* */ }
  }, []);
  const loadReport = useCallback(async () => {
    try { const { data } = await api.get("/admin/docs/access-report", { params: { limit: 500 } }); setReport(data); } catch (e) { toast.error("Could not load report"); }
  }, []);

  useEffect(() => { loadAccess(); }, [loadAccess]);
  useEffect(() => { if (sub === "vault") loadVault(); if (sub === "report") loadReport(); }, [sub, loadVault, loadReport]);

  const editRow = (r) => setForm({ email: r.value, docs_audience: r.docs_audience || "", doc_grants: r.doc_grants || [], doc_denies: r.doc_denies || [], nda_signed: !!r.nda_signed });
  const toggle = (key, id) => setForm((f) => ({ ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id] }));

  const saveAccess = async () => {
    if (!form.email.trim()) { toast.error("Email required"); return; }
    setBusy(true);
    try {
      await api.put("/admin/docs/access", form);
      toast.success(`Access saved for ${form.email}`);
      setForm(emptyForm); await loadAccess();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  const uploadVault = async () => {
    if (!vForm.file || !vForm.title.trim()) { toast.error("Title and file required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", vForm.file); fd.append("title", vForm.title); fd.append("audiences", vForm.audiences.join(","));
      await api.post("/admin/docs/vault", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Artefact uploaded to vault");
      setVForm({ title: "", audiences: [], file: null }); await loadVault();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };

  const exportCsv = () => {
    const head = ["at", "actor_email", "actor_role", "action", "target", "granted", "access"];
    const lines = [head.join(",")].concat((report.items || []).map((r) => head.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `docs-access-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  const SubTab = ({ id, label, icon: Icon }) => (
    <button onClick={() => setSub(id)} className={`px-3 py-1.5 text-sm flex items-center gap-1.5 border-b-2 ${sub === id ? "border-[#7737F6] ek-text" : "border-transparent ek-text-muted hover:text-zinc-800"}`} data-testid={`docs-access-subtab-${id}`}>
      <Icon size={14} /> {label}
    </button>
  );

  return (
    <div data-testid="docs-access-panel">
      <h2 className="text-xl font-semibold ek-text mb-1">Docs access &amp; vault</h2>
      <p className="text-sm ek-text-muted mb-4">Founder-only. Assign documentation tiers, per-document grant/deny, and manage NDA vault artefacts. Every reader action is audited.</p>

      <div className="flex gap-1 border-b ek-line mb-5">
        <SubTab id="access" label="Access" icon={ShieldCheck} />
        <SubTab id="vault" label="NDA Vault" icon={FolderLock} />
        <SubTab id="report" label="Access report" icon={BarChart3} />
      </div>

      {sub === "access" && (
        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <h3 className="text-sm font-semibold ek-text mb-2">Provisioned accounts</h3>
            <div className="border ek-line divide-y" data-testid="docs-access-list">
              {rows.length === 0 && <div className="p-3 text-sm ek-text-muted">No docs-only accounts yet.</div>}
              {rows.map((r) => (
                <button key={r.value} onClick={() => editRow(r)} className="w-full text-left p-3 hover:bg-zinc-50 flex items-center justify-between gap-2" data-testid={`docs-access-row-${r.value}`}>
                  <span className="min-w-0">
                    <span className="block text-sm ek-text truncate">{r.value}</span>
                    <span className="text-[11px] ek-text-muted">{r.docs_audience || r.platform_role || "—"}{r.nda_signed ? " · NDA" : ""}{(r.doc_denies || []).length ? ` · ${r.doc_denies.length} denied` : ""}{(r.doc_grants || []).length ? ` · ${r.doc_grants.length} granted` : ""}</span>
                  </span>
                  <span className="text-[11px] text-[#7737F6]">Edit</span>
                </button>
              ))}
            </div>
          </div>

          <div className="border ek-line p-4" data-testid="docs-access-form">
            <h3 className="text-sm font-semibold ek-text mb-3 flex items-center gap-1.5"><Plus size={14} /> Assign / edit access</h3>
            <label className="block text-xs ek-text-muted mb-1">Email</label>
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="person@company.com" className="w-full border ek-line px-2 py-1.5 text-sm mb-3" data-testid="docs-access-email" />

            <label className="block text-xs ek-text-muted mb-1">Audience tier</label>
            <select value={form.docs_audience} onChange={(e) => setForm({ ...form, docs_audience: e.target.value })} className="w-full border ek-line px-2 py-1.5 text-sm mb-3" data-testid="docs-access-tier">
              <option value="">— none (revoke) —</option>
              {tiers.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <div className="text-xs text-emerald-700 mb-1">Grant (extra docs)</div>
                {catalog.map((d) => (
                  <label key={d.id} className="flex items-center gap-1.5 text-[11px] ek-text-soft mb-0.5">
                    <input type="checkbox" checked={form.doc_grants.includes(d.id)} onChange={() => toggle("doc_grants", d.id)} data-testid={`docs-grant-${d.id}`} /> {d.id}
                  </label>
                ))}
              </div>
              <div>
                <div className="text-xs text-rose-700 mb-1">Deny (block docs)</div>
                {catalog.map((d) => (
                  <label key={d.id} className="flex items-center gap-1.5 text-[11px] ek-text-soft mb-0.5">
                    <input type="checkbox" checked={form.doc_denies.includes(d.id)} onChange={() => toggle("doc_denies", d.id)} data-testid={`docs-deny-${d.id}`} /> {d.id}
                  </label>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm ek-text-soft mb-4">
              <input type="checkbox" checked={form.nda_signed} onChange={(e) => setForm({ ...form, nda_signed: e.target.checked })} data-testid="docs-access-nda" /> NDA signed (vault access)
            </label>

            <div className="flex gap-2">
              <button onClick={saveAccess} disabled={busy} className="px-4 py-2 bg-[#7737F6] text-white text-sm disabled:opacity-50" data-testid="docs-access-save">Save access</button>
              {form.email && <button onClick={() => setForm(emptyForm)} className="px-3 py-2 border ek-line text-sm ek-text-muted">Clear</button>}
            </div>
          </div>
        </div>
      )}

      {sub === "vault" && (
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="border ek-line p-4" data-testid="vault-upload-form">
            <h3 className="text-sm font-semibold ek-text mb-3 flex items-center gap-1.5"><Upload size={14} /> Upload NDA artefact</h3>
            <input value={vForm.title} onChange={(e) => setVForm({ ...vForm, title: e.target.value })} placeholder="Title (e.g. SOC 2 Type II report 2026)" className="w-full border ek-line px-2 py-1.5 text-sm mb-3" data-testid="vault-title" />
            <div className="text-xs ek-text-muted mb-1">Audiences (who may request it)</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {tiers.map((t) => (
                <label key={t} className="flex items-center gap-1 text-[11px] ek-text-soft border ek-line px-1.5 py-0.5">
                  <input type="checkbox" checked={vForm.audiences.includes(t)} onChange={() => setVForm((f) => ({ ...f, audiences: f.audiences.includes(t) ? f.audiences.filter((x) => x !== t) : [...f.audiences, t] }))} data-testid={`vault-aud-${t}`} /> {t}
                </label>
              ))}
            </div>
            <input type="file" onChange={(e) => setVForm({ ...vForm, file: e.target.files?.[0] || null })} className="text-sm mb-3 block" data-testid="vault-file" />
            <button onClick={uploadVault} disabled={busy} className="px-4 py-2 bg-[#7737F6] text-white text-sm disabled:opacity-50" data-testid="vault-upload-btn">Upload to vault</button>
          </div>
          <div>
            <h3 className="text-sm font-semibold ek-text mb-2">Vault artefacts</h3>
            <div className="border ek-line divide-y" data-testid="vault-list">
              {vault.length === 0 && <div className="p-3 text-sm ek-text-muted">No artefacts yet.</div>}
              {vault.map((a) => (
                <div key={a.id} className="p-3 text-sm" data-testid={`vault-item-${a.id}`}>
                  <div className="ek-text">{a.title}</div>
                  <div className="text-[11px] ek-text-muted font-mono">{a.filename} · {Math.round((a.size || 0) / 1024)} KB · {(a.sha256 || "").slice(0, 12)}…</div>
                  <div className="text-[11px] ek-text-muted">audiences: {(a.audiences || []).join(", ") || "—"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {sub === "report" && (
        <div data-testid="docs-access-report">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold ek-text">Who opened what ({report.count || 0} events)</h3>
            <button onClick={exportCsv} disabled={!report.items?.length} className="px-3 py-1.5 border ek-line text-sm flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="report-csv-btn"><Download size={14} /> Export CSV</button>
          </div>
          {(report.summary || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {report.summary.map((s) => (
                <span key={s.target} className="text-[11px] border ek-line px-2 py-1 ek-text-soft">{s.target}: <b>{s.reads}</b> reads · {s.unique_readers} readers</span>
              ))}
            </div>
          )}
          <div className="border ek-line overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="ek-surface-2 ek-text-muted"><tr>
                <th className="text-left p-2">When</th><th className="text-left p-2">Who</th><th className="text-left p-2">Tier</th><th className="text-left p-2">Action</th><th className="text-left p-2">Document</th><th className="text-left p-2">Result</th>
              </tr></thead>
              <tbody>
                {(report.items || []).map((r, i) => (
                  <tr key={i} className="border-t ek-line" data-testid="report-row">
                    <td className="p-2 ek-text-muted whitespace-nowrap">{fmtDate(r.at)}</td>
                    <td className="p-2 ek-text">{r.actor_email}</td>
                    <td className="p-2 ek-text-muted">{r.actor_role || "—"}</td>
                    <td className="p-2 ek-text-muted">{r.action}</td>
                    <td className="p-2 ek-text-soft">{r.target}</td>
                    <td className={`p-2 ${r.granted ? "text-emerald-600" : "text-rose-600"}`}>{r.granted ? "granted" : "denied"}{r.access ? ` (${r.access})` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
